import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import infoKnockoutTokens from '@amzn/meridian-tokens/base/icon/info-knockout';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Tooltip from '@amzn/meridian/tooltip';
import React, { useState } from 'react';
import { OptionalExternalLink, TranslationFunction } from '../../components/blocks';
import { ResellerItem, SalesOrderDemandShipment } from '../../redux/types';
import { formatDateIsoOrBlank } from '../../utils/dateUtils';
import { AsinDetails } from './AsinDetails';
import { getVisibleShipmentColumns } from './shipmentUtils';

export const ShipmentTable = ({
    shipmentDetails,
    expandedShipments,
    toggleShipment,
    resellerItems,
    expandAllShipments,
    collapseAllShipments,
    requestedShipDate,
    requestedDeliveryDate,
    totalUnits,
    t,
    timezone,
    canUploadAttachments,
    onUploadAttachments,
}: {
    shipmentDetails: SalesOrderDemandShipment[];
    expandedShipments: string[];
    toggleShipment: (shipmentId: string) => void;
    resellerItems: ResellerItem[];
    expandAllShipments: () => void;
    collapseAllShipments: () => void;
    requestedShipDate?: number;
    requestedDeliveryDate?: number;
    totalUnits: number;
    t: TranslationFunction;
    timezone?: string;
    canUploadAttachments?: boolean;
    onUploadAttachments?: (shipmentId: string) => void;
}) => {
    const [sortColumn, setSortColumn] = useState<string>();
    const [sortDirection, setSortDirection] = useState<'ascending' | 'descending'>();

    const visibleColumns = getVisibleShipmentColumns(shipmentDetails);

    const handleSort = ({ sortColumn, sortDirection }: { sortColumn: string; sortDirection: string }) => {
        setSortColumn(sortColumn);
        setSortDirection(sortDirection as 'ascending' | 'descending');
    };

    const sortedShipmentDetails = [...shipmentDetails].sort((a, b) => {
        if (!sortColumn) return 0;
        if (sortColumn === 'shipmentId') {
            const result = a.shipmentId.localeCompare(b.shipmentId, undefined, { numeric: true });
            return sortDirection === 'ascending' ? result : -result;
        }
        const aVal = a[sortColumn as keyof SalesOrderDemandShipment];
        const bVal = b[sortColumn as keyof SalesOrderDemandShipment];
        if (typeof aVal === 'string' && typeof bVal === 'string') {
            return sortDirection === 'ascending' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        }
        if (typeof aVal === 'number' && typeof bVal === 'number') {
            return sortDirection === 'ascending' ? aVal - bVal : bVal - aVal;
        }
        return 0;
    });

    const toggleExpandAll = () => {
        const allExpanded = shipmentDetails.every((s) => expandedShipments.includes(s.shipmentId!));
        if (allExpanded) collapseAllShipments();
        else expandAllShipments();
    };

    const renderTableRows = () => {
        const rows: React.ReactElement[] = [];
        sortedShipmentDetails.forEach((shipment) => {
            rows.push(
                <TableRow key={shipment.shipmentId} highlightOnHover={true}>
                    <TableCell>
                        <Button type={'icon'} size={'small'} onClick={() => toggleShipment(shipment.shipmentId!)}>
                            <Icon
                                tokens={
                                    expandedShipments.includes(shipment.shipmentId!)
                                        ? chevronDownSmallTokens
                                        : chevronRightSmallTokens
                                }
                            >
                                {expandedShipments.includes(shipment.shipmentId!)
                                    ? t('collapse-shipmentDetails')
                                    : t('expand-shipmentDetails')}
                            </Icon>
                        </Button>
                    </TableCell>
                    <TableCell>{shipment.shipmentId}</TableCell>
                    <TableCell>{shipment.units}</TableCell>
                    {visibleColumns.hasStatus && <TableCell>{shipment.status || ''}</TableCell>}
                    <TableCell>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            {shipment.carrierSCAC || ''}
                            {shipment.carrierSCAC === 'AZNG' && (
                                <Tooltip position={'top'} title={t('amazonFreightOrder-tooltip')}>
                                    <Icon tokens={infoKnockoutTokens} />
                                </Tooltip>
                            )}
                        </span>
                    </TableCell>
                    {visibleColumns.hasTrackingNumber && (
                        <TableCell>
                            <OptionalExternalLink href={shipment.trackingUrl} tooltip={t('trackingLink-tooltip')}>
                                {shipment.trackingNumber || ''}
                            </OptionalExternalLink>
                        </TableCell>
                    )}
                    <TableCell>{formatDateIsoOrBlank(requestedShipDate, timezone)}</TableCell>
                    <TableCell>{formatDateIsoOrBlank(shipment.shipDate, timezone)}</TableCell>
                    <TableCell>{formatDateIsoOrBlank(requestedDeliveryDate, timezone)}</TableCell>
                    {visibleColumns.hasDeliveryDate && (
                        <TableCell>{formatDateIsoOrBlank(shipment.deliveryDate, timezone)}</TableCell>
                    )}
                    {canUploadAttachments && (
                        <TableCell>
                            <Button
                                type={'primary'}
                                size={'small'}
                                onClick={() => onUploadAttachments?.(shipment.shipmentId!)}
                                data-testid={`shipmentTable.uploadAttachmentsButton.${shipment.shipmentId}`}
                            >
                                {t('uploadAttachments-button')}
                            </Button>
                        </TableCell>
                    )}
                </TableRow>
            );

            if (expandedShipments.includes(shipment.shipmentId!)) {
                const columnSpan =
                    7 +
                    (visibleColumns.hasStatus ? 1 : 0) +
                    (visibleColumns.hasTrackingNumber ? 1 : 0) +
                    (visibleColumns.hasDeliveryDate ? 1 : 0) +
                    (canUploadAttachments ? 1 : 0);

                rows.push(
                    <TableRow key={`${shipment.shipmentId}-items-subtable`} backgroundColor={'secondary'}>
                        <TableCell columnSpan={columnSpan}>
                            <AsinDetails items={shipment.items || []} resellerItems={resellerItems} t={t} />
                        </TableCell>
                    </TableRow>
                );
            }
        });
        return rows;
    };

    return (
        <Column>
            <Table
                headerRows={1}
                showDividers={true}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
                data-testid={'poViewShipmentTable'}
            >
                <TableRow>
                    <TableCell width={60}>
                        {shipmentDetails.length > 0 && (
                            <Button type={'icon'} size={'small'} onClick={toggleExpandAll}>
                                <Icon
                                    tokens={
                                        expandedShipments.length === shipmentDetails.length
                                            ? chevronDownSmallTokens
                                            : chevronRightSmallTokens
                                    }
                                >
                                    {t('purchaseOrderListPage:expandCollapseAll-label')}
                                </Icon>
                            </Button>
                        )}
                    </TableCell>
                    <TableCell sortColumn={'shipmentId'}>{t('shipmentId-column')}</TableCell>
                    <TableCell sortColumn={'units'}>{t('units-column')}</TableCell>
                    {visibleColumns.hasStatus && <TableCell sortColumn={'status'}>{t('status-column')}</TableCell>}
                    <TableCell sortColumn={'carrier'}>{t('carrier-column')}</TableCell>
                    {visibleColumns.hasTrackingNumber && (
                        <TableCell sortColumn={'trackingNumber'}>{t('trackingNo-column')}</TableCell>
                    )}
                    <TableCell sortColumn={'requestedShipDate'}>{t('requestedShipDate-column')}</TableCell>
                    <TableCell sortColumn={'shipDate'}>{t('shipDate-column')}</TableCell>
                    <TableCell sortColumn={'requestedDeliveryDate'}>{t('requestedDeliveryDate-column')}</TableCell>
                    {visibleColumns.hasDeliveryDate && (
                        <TableCell sortColumn={'deliveryDate'}>{t('deliveryDate-column')}</TableCell>
                    )}
                    {canUploadAttachments && <TableCell>{t('attachments-column')}</TableCell>}
                </TableRow>
                <TableRow key={'totals'}>
                    <TableCell />
                    <TableCell>
                        <strong>
                            <em>{t('common:total-label')}</em>
                        </strong>
                    </TableCell>
                    <TableCell>
                        <strong>{totalUnits}</strong>
                    </TableCell>
                    {visibleColumns.hasStatus && <TableCell />}
                    <TableCell />
                    {visibleColumns.hasTrackingNumber && <TableCell />}
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    {visibleColumns.hasDeliveryDate && <TableCell />}
                    {canUploadAttachments && <TableCell />}
                </TableRow>
                {renderTableRows()}
            </Table>
        </Column>
    );
};
